import React, { useState, useEffect, useRef } from "react";
import { ExternalLink } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { isValidLink, toEntries } from "./SourceLinksField";

// Widest the popover is allowed to get, so a long URL can't blow out the row. Sized from the
// real URL forms in use: a .../destaque/<hash> needs 416px and the longest campaign slug 438px
// at this font, and at 360 every one of them ellipsized to the same prefix, which made links
// that differ only in their tail impossible to tell apart. The panel is shrink-to-fit, so this
// is only a ceiling: a list of short labels still renders narrow.
const PANEL_WIDTH = 480;
// Tallest the link list gets before it scrolls inside the panel, so the last entries
// stay reachable: scrolling the page closes the popover, so they'd be unreachable otherwise.
const PANEL_MAX_HEIGHT = 240;

// Valid links of a file, in order, as {url, label}. Handles files with no links at all,
// and files whose entries are still bare strings.
const fileLinks = (file) =>
  toEntries(file.metadata?.source_urls ?? []).filter((entry) => isValidLink(entry.url));

// Same shape the backend's related_links_block produces, so the two stay recognisably one format.
const entryTitle = (entry) => (entry.label ? `${entry.label}: ${entry.url}` : entry.url);

/**
 * Source link affordance for one file row.
 *
 * One link renders a plain anchor that opens it directly. Two or more render a
 * button that opens a popover listing every link, each individually clickable:
 * the previous behaviour (first link as href, all links in a `title` tooltip)
 * was unreachable by keyboard and absent on touch, so only the first link was
 * ever actually openable there.
 */
const FileSourceLinks = ({ file }) => {
  const { translate } = useLanguage();
  const links = fileLinks(file);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on click outside, scroll or Escape.
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      // Don't close if clicking inside the panel
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      // Nor on the trigger: mousedown here would close, and the click that follows would
      // reopen, so clicking the trigger to dismiss would never work. The onClick toggles it.
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const closeOnScroll = (e) => {
      // The link list scrolls inside the panel; only page scroll should dismiss.
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const closeOnEscape = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      if (triggerRef.current) triggerRef.current.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    // Use setTimeout to avoid closing immediately on the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", close);
      document.addEventListener("scroll", closeOnScroll, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [open]);

  if (links.length === 0) return null;

  const label = (
    <>
      <ExternalLink className="h-3.5 w-3.5 mr-1" />
      <span className="text-xs">
        {translate("sourceLinkLabel")}
        {links.length > 1 ? ` (${links.length})` : ""}
      </span>
    </>
  );

  const triggerClass =
    "inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-mint)]";

  // Single link: open it directly, no popover.
  if (links.length === 1) {
    return (
      <a
        href={links[0].url}
        target="_blank"
        rel="noopener noreferrer"
        title={entryTitle(links[0])}
        className={triggerClass}
        onClick={(e) => e.stopPropagation()}
      >
        {label}
      </a>
    );
  }

  // The wrapper keeps the panel out of the parent flex row: `space-x-6` there
  // would otherwise put a margin-left on the panel and shift it sideways.
  return (
    <span className="inline-flex items-center">
      <button
        type="button"
        ref={triggerRef}
        aria-haspopup="true"
        aria-expanded={open}
        className={triggerClass}
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          // Anchor to the trigger's left edge, clamped so the panel stays on screen.
          const left = Math.min(
            rect.left,
            Math.max(8, window.innerWidth - PANEL_WIDTH - 8)
          );
          setPosition({ top: rect.bottom + 4, left });
          setOpen(true);
        }}
      >
        {label}
      </button>
      {open && (
        <div
          ref={panelRef}
          className="fixed rounded-xl shadow-lg z-[9999] py-1"
          style={{
            backgroundColor: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            top: position.top,
            left: position.left,
            maxWidth: `min(${PANEL_WIDTH}px, calc(100vw - 16px))`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="px-3 py-1.5 text-xs font-semibold"
            style={{
              color: "var(--color-text-muted)",
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            {translate("sourceUrlsLabel")}
          </div>
          <div
            style={{
              maxHeight: PANEL_MAX_HEIGHT,
              overflowY: "auto",
              // Without this, reaching the end of the list chains the scroll to the page,
              // and the page-scroll listener closes the popover under the user's cursor.
              overscrollBehavior: "contain",
            }}
          >
            {links.map((entry, i) => (
              <a
                key={`${i}-${entry.url}`}
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                title={entryTitle(entry)}
                className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[var(--color-surface-raised)] transition-colors"
                style={{ color: "var(--color-text-secondary)" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{entry.label || entry.url}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </span>
  );
};

export default FileSourceLinks;
